import React from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import ItemCommitsList from './ItemCommitsList';
import ItemFormFields from './ItemFormFields';
import ItemSentInfo from '../ItemSentInfo';
import ItemStatusChips from '../ItemStatusChips';
import useItemForm from '../../hooks/useItemForm';

/**
 * Criar ou editar uma novidade do catálogo. O status muda pelas ações em lote da tabela;
 * rascunho também pode sair daqui já revisado ("Salvar e marcar revisado").
 * @param {{ open: boolean, item: Object|null, onClose: Function, onSaved: Function,
 *   onSavedAndReviewed: Function, onDiscard: Function }} props
 *   item null com open = nova novidade
 */
function ItemEditDialog({ open, item, onClose, onSaved, onSavedAndReviewed, onDiscard }) {
  const { form, setField, save, isSaving, error } = useItemForm(item, open);
  const isNew = !item;
  const isDraft = isNew || item.status === 'draft';
  // Descartar: nunca publicado nem enviado (o servidor confere de novo)
  const canDiscard = !isNew && !['published', 'discarded'].includes(item.status)
    && !(item.editions || []).some((edition) => ['sending', 'sent'].includes(edition.status));

  return (
    <Dialog open={open} onClose={isSaving ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="caption" color="text.secondary" component="div">
          {isNew ? 'Nova novidade' : item.slug}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Typography variant="subtitle1" component="div" sx={{ fontWeight: 600 }}>
            {isNew ? 'Criar novidade' : 'Editar novidade'}
          </Typography>
          {!isNew && <ItemStatusChips item={item} />}
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <ItemFormFields form={form} setField={setField} isNew={isNew} />
        {!isNew && (
          <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid #EEF1F6', display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '2fr 1fr' } }}>
            <ItemCommitsList commits={item.commits} />
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Envios</Typography>
              <ItemSentInfo item={item} />
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3 }}>
        {canDiscard && (
          <Button color="error" onClick={() => onDiscard(item)} disabled={isSaving} sx={{ mr: 'auto' }}>
            Descartar
          </Button>
        )}
        <Button onClick={onClose} disabled={isSaving}>Cancelar</Button>
        {isDraft && (
          <Button variant="outlined" onClick={() => save(onSavedAndReviewed)} disabled={isSaving}>
            Salvar e marcar revisado
          </Button>
        )}
        <Button variant="contained" onClick={() => save(onSaved)} disabled={isSaving}>
          {isSaving ? 'Salvando...' : 'Salvar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ItemEditDialog;
